import { createContext, useContext, useState, useRef, useEffect } from "react";
import { useRouter, useSearchParams, usePathname } from "@/lib/navigation";
import debounce from "lodash/debounce";
import { searchBooks } from "../services/googleBooksApi";
import { searchUsers, searchLists } from "../firebase/services/searchServices";
import { trackSearchTerm } from "../firebase/services/analyticsServices";
import { trackSearch } from "../firebase/services/analyticsServices";

const SearchContext = createContext();

export function SearchProvider({ children }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [activeTab, setActiveTabState] = useState(
    searchParams.get("tab") || "books"
  );
  const [books, setBooks] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filters, setFiltersState] = useState({
    genre: null,
    sort: null,
  });

  // Cache for search results
  const resultsCache = useRef({
    books: new Map(),
    users: new Map(),
    lists: new Map(),
  });

  const abortControllerRef = useRef(null);
  const loadingRef = useRef(null);
  const filtersRef = useRef(filters);

  // Function to get cache key (includes filters for books)
  const getCacheKey = (query, tab) => {
    if (tab === "books") {
      const currentFilters = filtersRef.current;
      const filterKey = `${currentFilters.genre || ""}:${currentFilters.sort || ""}`;
      return `${query}:${tab}:${filterKey}`;
    }
    return `${query}:${tab}`;
  };

  // Function to check if results are cached
  const getCachedResults = (query, tab) => {
    const key = getCacheKey(query, tab);
    if (!resultsCache.current[tab]) {
      return null;
    }
    return resultsCache.current[tab].get(key);
  };

  // Function to cache results
  const cacheResults = (query, tab, results) => {
    const key = getCacheKey(query, tab);
    if (!resultsCache.current[tab]) {
      resultsCache.current[tab] = new Map();
    }
    resultsCache.current[tab].set(key, {
      results,
      timestamp: Date.now(),
    });
  };

  // Function to perform search for a specific tab
  const performSearch = async (query, tab, signal, currentPage = 1) => {
    const cached = getCachedResults(query, tab);
    if (
      cached &&
      currentPage === 1 &&
      Date.now() - cached.timestamp < 5 * 60 * 1000
    ) {
      return cached.results;
    }

    const startIndex = (currentPage - 1) * 20;
    let results;

    switch (tab) {
      case "books":
        results = await searchBooks(query, startIndex, 20, signal, filtersRef.current);
        break;
      case "users":
        results = await searchUsers(query, startIndex, 20);
        break;
      case "lists":
        results = await searchLists(query, startIndex, 20);
        break;
    }

    if (!signal?.aborted && currentPage === 1) {
      cacheResults(query, tab, results);
    }
    return results;
  };

  // Function to preload other tabs
  const preloadOtherTabs = async (query, currentTab) => {
    const tabOrder = {
      books: ["users", "lists"],
      users: ["books", "lists"],
      lists: ["books", "users"],
    };

    // Defensive check: ensure currentTab is valid
    if (!tabOrder[currentTab]) {
      return;
    }

    for (const tab of tabOrder[currentTab]) {
      try {
        await performSearch(query, tab);
      } catch {
        // Silently fail preload
      }
    }
  };

  // Add effect to handle initial load
  useEffect(() => {
    const query = searchParams.get("q");
    const tabFromUrl = searchParams.get("tab");

    // Validate tab parameter
    const validTabs = ["books", "users", "lists"];
    const tab = validTabs.includes(tabFromUrl) ? tabFromUrl : "books";

    if (query) {
      setSearchQuery(query);
      setActiveTabState(tab);
      debouncedSearch(query, tab, true);
    }
  }, [searchParams]); // Only run on mount and when searchParams changes

  // Store search state in sessionStorage for back navigation
  useEffect(() => {
    if (searchQuery) {
      sessionStorage.setItem("lastSearchQuery", searchQuery);
      sessionStorage.setItem("lastSearchTab", activeTab);
    }
  }, [searchQuery, activeTab]);

  // Restore search state when coming back to search page
  useEffect(() => {
    if (pathname === "/search" && !searchParams.get("q") && !searchQuery) {
      const savedQuery = sessionStorage.getItem("lastSearchQuery");
      const savedTab = sessionStorage.getItem("lastSearchTab");

      // Validate saved tab
      const validTabs = ["books", "users", "lists"];
      const activeTabToUse = validTabs.includes(savedTab) ? savedTab : "books";

      if (savedQuery) {
        setSearchQuery(savedQuery);
        setActiveTabState(activeTabToUse);
        debouncedSearch(savedQuery, activeTabToUse);
      }
    }
  }, [pathname, searchParams]);

  const debouncedSearch = useRef(
    debounce(async (query, tab) => {
      if (!query.trim()) {
        setBooks([]);
        setUsers([]);
        setLists([]);
        return;
      }

      setLoading(true);
      setError(null);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const results = await performSearch(query, tab, abortController.signal);

        if (!abortController.signal.aborted) {
          // Update results
          switch (tab) {
            case "books":
              setBooks(results || []);
              break;
            case "users":
              setUsers(results || []);
              break;
            case "lists":
              setLists(results || []);
              break;
          }
          setHasMore(results?.length === 20);

          // Update URL and track analytics only after successful search
          const params = new URLSearchParams(searchParams.toString());
          params.delete("q");
          params.set("tab", tab);
          router.push(`/search?${params.toString()}`);

          // Track both the general search and the specific term
          try {
            await Promise.all([
              trackSearch(null, tab, query.trim()), // Track search with type
              trackSearchTerm(query.trim()), // Track specific term
            ]);
          } catch {
            // Don't throw - we don't want analytics errors to affect the user experience
          }

          // Preload other tabs
          preloadOtherTabs(query, tab);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
          abortControllerRef.current = null;
        }
      }
    }, 300)
  ).current;

  const handleSearch = (newQuery) => {
    setSearchQuery(newQuery);
    debouncedSearch(newQuery, activeTab);
    // Scroll to top when search query changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTabChange = (newTab) => {
    // 1. Clear previous results of other tabs
    switch (newTab) {
      case "books":
        setUsers([]);
        setLists([]);
        break;
      case "users":
        setBooks([]);
        setLists([]);
        break;
      case "lists":
        setBooks([]);
        setUsers([]);
        break;
    }

    // 2. Update active tab state immediately
    setActiveTabState(newTab);

    // 3. Update URL (non-blocking)
    const params = new URLSearchParams(searchParams.toString());
    params.delete("q");
    params.set("tab", newTab);
    router.push(`/search?${params.toString()}`);

    // 4. Only perform search if we have a query
    if (searchQuery) {
      const cached = getCachedResults(searchQuery, newTab);
      if (cached) {
        // Use cached results immediately
        switch (newTab) {
          case "books":
            setBooks(cached.results);
            break;
          case "users":
            setUsers(cached.results);
            break;
          case "lists":
            setLists(cached.results);
            break;
        }
        // Refresh cache in background
        performSearch(searchQuery, newTab, null).catch(() => {});
      } else {
        // Perform new search
        debouncedSearch(searchQuery, newTab);
      }
    }
  };

  const retrySearch = () => {
    setIsRetrying(true);
    debouncedSearch(searchQuery, activeTab);
    setIsRetrying(false);
  };

  const loadMore = async () => {
    if (isLoadingMore || !hasMore || !searchQuery) return;

    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const results = await performSearch(
        searchQuery,
        activeTab,
        null,
        nextPage
      );

      if (results?.length) {
        switch (activeTab) {
          case "books":
            setBooks((prev) => [...prev, ...results]);
            break;
          case "users":
            setUsers((prev) => [...prev, ...results]);
            break;
          case "lists":
            setLists((prev) => [...prev, ...results]);
            break;
        }
        setHasMore(results.length === 20);
        setPage(nextPage);
      } else {
        setHasMore(false);
      }
    } catch {
      // Silently fail load more
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Update filtersRef whenever filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const setFilters = (type, value) => {
    setFiltersState((prev) => ({
      ...prev,
      [type]: value,
    }));
    // Reset pagination when filters change
    setPage(1);
  };

  // Trigger search when filters change
  useEffect(() => {
    if (searchQuery && activeTab === "books") {
      // Reset results and search with new filters
      setBooks([]);
      debouncedSearch(searchQuery, activeTab);
    }
  }, [filters.genre, filters.sort]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedSearch]);

  const value = {
    searchQuery,
    setSearchQuery: handleSearch,
    activeTab,
    setActiveTab: handleTabChange,
    books,
    users,
    lists,
    loading,
    error,
    hasMore,
    loadingRef,
    isRetrying,
    retrySearch,
    loadMore,
    isLoadingMore,
    filters,
    setFilters,
  };

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
}
